<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader == true"
      variant="dark"
      opacity="0.85"
      blur="2px"
      spinner-variant="primary"
      spinner-small
      rounded="sm"
    >
      <alert-handler :alert-errors="alertErrors" />
      <b-card
        aria-hidden="true"
        class="mb-0"
        title="Personal Information"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >

          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>

              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="userData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-group
                    :label="$t('DEBTIA_EMAIL')"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="userData.email"
                      type="email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Phone"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PHONE')"
                    label-for="phone"
                  >
                    <b-form-input
                      id="phone"
                      v-model="userData.phone"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Contact Person -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CONTACT_PERSON')"
                  label-for="contact-person"
                >
                  <b-form-input
                    id="contact-person"
                    v-model="userData.contact_person"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Client Type -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CLIENT_TYPE')"
                  label-for="client-type"
                >
                  <v-select
                    v-model="userData.type"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="typeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="client-type"
                    :value="'private-person'"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Company Type -->
              <b-col
                v-if="userData.type === 'company'"
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Company Type"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_COMPANY_TYPE')"
                    label-for="company-type"
                  >
                    <b-form-input
                      id="company-type"
                      v-model="userData.company_type"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: CVRCPR -->
              <b-col
                v-if="userData.type !== 'person'"
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="CVR"
                  :rules="userData.address.country_id === 247 ? 'integer|length:8' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CVRCPR')"
                    label-for="cvrcpr"
                  >
                    <b-form-input
                      id="cvrcpr"
                      v-model="userData.cvrcpr"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

              </b-col>

              <!-- Field: CPR -->
              <b-col
                v-if="userData.type === 'person'"
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CPR')"
                  label-for="cpr"
                >
                  <cleave
                    id="cpr"
                    v-model="userData.cpr"
                    class="form-control"
                    :raw="false"
                    :options="options.cprDelimiter"
                  />
                </b-form-group>

              </b-col>

            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_ADDRESS') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="House Number"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="house-number"
                  >
                    <b-form-input
                      id="house-number"
                      v-model="userData.address.house_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Road Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ROAD_NAME')"
                    label-for="road-name"
                  >
                    <b-form-input
                      id="road-name"
                      v-model="userData.address.road_name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_PLACE_NAME')"
                  label-for="place"
                >
                  <b-form-input
                    id="place"
                    v-model="userData.address.place"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Zip Code"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ZIP_CODE')"
                    label-for="zip-code"
                  >
                    <b-form-input
                      id="zip-code"
                      v-model="userData.address.zip_code"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getCityName($event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="City"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CITY')"
                    label-for="city"
                  >
                    <b-form-input
                      id="city"
                      v-model="userData.address.city"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getZipCode($event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  v-slot="{ errors, validated, failed }"
                  name="Country"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_COUNTRY')"
                    label-for="select-country"
                    :state="failed ? false : (validated ? true : null)"
                  >
                    <v-select
                      id="select-country"
                      v-model="userData.address.country_id"
                      :disabled="busy"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="countries"
                      :reduce="val => val.value"
                      :clearable="true"
                      placeholder="Select Country"
                      :class="{ 'is-invalid': errors.length > 0 }"
                      @input="onCountryChange"
                    >
                      <template #selected-option-container="{ option }">
                        <div class="vs__selected">
                          {{ $t(option.label) }}
                        </div>
                      </template>

                      <template #option="{ label }">
                        {{ $t(label) }}
                      </template>
                    </v-select>

                    <b-form-invalid-feedback v-if="errors[0]">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

            </b-row>

            <hr>

            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              {{ $t('DEBTIA_SAVE') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              {{ $t('DEBTIA_RESET') }}
            </b-button>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BForm,
  BCard,
  BFormInvalidFeedback,
  BOverlay,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import formValidation from '@core/comp-functions/forms/form-validation'
import { ref, reactive } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'
import constants from '@/app-configs/constants'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { required, alphaNum, email } from '@validations'
import router from '@/router'
import $this from 'vue'
import Cleave from 'vue-cleave-component'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cleave.js/dist/addons/cleave-phone.us'
import countries from '@/countries'
import debtorStoreModule from '../debtorStoreModule'
import AlertHandler from './AlertHandler.vue'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BCard,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    AlertHandler,
    BOverlay,
    Cleave,
  },

  data() {
    return {
      busy: false,
      // userType: JSON.parse(localStorage.getItem('userData')),
      countries,
      required,
      alphaNum,
      email,
      options: {
        cprDelimiter: {
          delimiters: ['-'],
          blocks: [6, 4],
          uppercase: true,
        },
      },
    }
  },

  setup() {
    const blankUserData = {
      email: '',
      name: '',
      username: '',
      loginStatus: '1',
      phone: null,
      // password: null,
      type: 'person',
      cvrcpr: null,
      cpr: null,
      address: {
        road_name: null,
        house_number: null,
        place: null,
        zip_code: null,
        city: null,
        country_id: 247,
      },
    }

    const userData = reactive(blankUserData)
    const resetuserData = () => {
      userData.value = JSON.parse(JSON.stringify(blankUserData))
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetuserData)

    const loginOptions = [
      { label: 'Allowed', value: '1' },
      { label: 'Not Allowed', value: '0' },
    ]
    const typeOptions = [
      { label: 'Person', value: 'person' },
      { label: 'Sole Proprietorship', value: 'sole-proprietorship' },
      { label: 'Capital Company', value: 'capital-company' },
    ]
    const alertErrors = ref([])

    // const validateUsername = ref(true)

    const loader = ref(false)

    const onSubmit = async () => {
      const data = { ...userData }
      loader.value = true
      // const validation = await axios.post('debtor/validate-username', { username: data.username })
      //   .then(() => true).catch(() => false)

      // if (!validation) {
      //   validateUsername.value = validation
      //   loader.value = false
      //   return false
      // }
      // this.loader = true
      const USER_APP_STORE_MODULE_NAME = 'app-debtor'

      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, debtorStoreModule)

      store.dispatch('app-debtor/addDebtor', data)
        .then(() => {
          loader.value = true
          // validateUsername.value = true
          router.replace('/debtors/list')
            .then(() => {
              $this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Debtor Added',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'Debtor successfully Added!',
                },
              })
            })
        })
        .catch(error => {
          alertErrors.value = []
          if (typeof error.data.message === 'string') {
            alertErrors.value.push(error.data.message)
          } else {
            alertErrors.value.push(...error.data.message)
          }
          loader.value = false
        })
      return true
    }

    return {
      loginOptions,
      refFormObserver,
      getValidationState,
      resetForm,
      userData,
      // validateUsername,
      onSubmit,
      alertErrors,
      typeOptions,
      loader,
    }
  },
  created() {
    this.createUsername()
  },
  methods: {
    async createUsername() {
      const url = `${constants.debtiaappUrl}/create-validate/username`
      await axios.post(url, {
        type: 'debtor',
      }).then(response => {
        if (response.status === 200) {
          this.userData.username = response.data.data.username
        }
      })
    },
    getCityName(event) {
      const countryId = this.userData.address.country_id
      if (countryId !== 247) return
      axios.get(`${constants.debtiaappUrl}/settings/get-city-name-by-code?code=${event}&country_id=${countryId}`)
        .then(response => {
          this.userData.address.city = response.data.data.city_name
        }).catch(error => {
          console.log(error)
        })
    },
    getZipCode(event) {
      axios.get(`${constants.debtiaappUrl}/settings/get-zip-code-by-city-name?city=${event}`)
        .then(response => {
          if (response.data.data.zip_code !== null) {
            this.userData.address.zip_code = response.data.data.zip_code
          }
        }).catch(error => {
          console.log(error)
        })
    },
    onCountryChange(val) {
      if (val) {
        this.userData.address.city = null
      }
    },
  },
}
</script>
